/**
 * Course-complete celebration — Sprint 4.
 *
 * Shown when the candidate has just passed the final exam. The certificate
 * service (FR-H / Sprint 5) takes over from here; this page is the
 * "you did the thing" handshake between exam-pass and next-steps.
 *
 * No backend dependency — the page is a static congratulations + a
 * link onward to the capstone (the last credentialing step).
 */
import * as React from "react";
import { Link, useParams } from "react-router-dom";

export function CourseCompletePage(): React.JSX.Element {
  const { courseSlug = "cait-foundations" } = useParams<{ courseSlug: string }>();
  return (
    <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
      <p className="text-sm font-semibold uppercase tracking-wider text-emerald-700">
        Foundations Certificate · Final Exam Passed
      </p>
      <h1 className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
        You did it.
      </h1>
      <p className="mt-6 text-lg text-foreground">
        Every module quiz cleared, every domain floor met on the final
        exam. The CaiT Foundations coursework is behind you.
      </p>
      <p className="mt-4 text-base text-foreground">
        One step left to the credential: the capstone essay (1500–3000
        words on integrating Cognitive AI Therapy into your practice).
        It's graded against the published rubric and you have up to two
        attempts.
      </p>

      <article className="mt-10 rounded-lg border border-emerald-200 bg-emerald-50 p-6">
        <h2 className="text-lg font-semibold text-foreground">
          Next: capstone essay
        </h2>
        <p className="mt-2 text-sm text-foreground">
          Drafts save without locking. Submit when you're ready; grading
          happens out-of-band and lands by email.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to={`/capstone/${courseSlug}`}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Start capstone essay →
          </Link>
          <Link
            to="/journal"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Re-read my journal
          </Link>
        </div>
      </article>

      <article className="mt-8 rounded-lg border border-border bg-white p-6 text-sm text-muted-foreground">
        <h3 className="font-semibold text-foreground">After the capstone</h3>
        <p className="mt-2">
          Once your capstone is graded, your Foundations Certificate is
          issued and posted to your verification page. You'll be emailed a
          shareable link and a printable PDF.
        </p>
      </article>
    </section>
  );
}
